import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import ContactUsAtacama from '../../components/ContactUs/ContactUsAtacama';
import { images } from '../../components/shared/assets';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import './MeetingsEventsAtacama.css';

const MeetingsEventsAtacama: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const [selectedEventType, setSelectedEventType] = useState<string | undefined>(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
    
    // Initialize the drag scroll for the carousel
    initializeDragScroll('draggable-carousel');
    
    // FilterBar component handles filter functionality
    // No manual event listeners needed
  }, []);

  return (
    <div className="meetings-events-atacama">
      {/* Navigation - Atacama variant */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Hero Section with Diptych - Full Screen 50/50 */}
      <section className="meetings-hero-diptych" data-scroll-section>
        <div className="meetings-hero-text-block">
          <p className="meetings-hero-description">
            In the remote corners of the American continent — where guardian mountains rise, eternal ice lingers, stars burn brighter, and the jungle whispers with wonder — Awasi Experiences was born to curate your journey.
          </p>
        </div>
        <div 
          className="meetings-hero-image-block"
          style={{ backgroundImage: `url('/assets/images/meetings-events/c3f45798b7dc03d9f0fb733a9b4a55b94b3c083b.png')` }}
        />
      </section>

      {/* Event Spaces Gallery with Filters */}
      <section className="meetings-occasions-section" data-scroll-section>
        <div className="meetings-occasions-content">
          {/* Filter Section - Matching Figma exactly */}
          <div className="meetings-filter-buttons">
            <button className="meetings-filter-btn">
              AWASI ATACAMA +
            </button>
            <button className="meetings-filter-btn">
              TYPE +
            </button>
          </div>

          {/* Occasions Gallery - 3x2 Grid matching Figma exactly */}
          <div className="meetings-occasions-gallery">
            {/* Row 1 */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/meetings-events/e148a3c9b1b41cdcfe55bca79640b870fcb427c8.png" alt="Birthdays" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Birthdays</h3>
                <p>Morbi tincidunt sagittis tristique dignissim nunc gravida lectus sagittis. Vulputate elementum massa dictum eleifend at faucibus tempor. Fermentum nunc id donec.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/meetings-events/3c040adef04baeda73725b5b017494444ef32dec.png" alt="Group Trips" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Group Trips</h3>
                <p>Morbi faucibus urna cras arcu id elementum. Libero leo nam sollicitudin dui faucibus vitae. Eu vitae mollis in ut eget lorem vitae phasellus faucibus. In et arcu venenatis.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/meetings-events/157764183566073919c0adb5caa263ab6baf7a8e.png" alt="Weddings & Celebrations" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Weddings & Celebrations</h3>
                <p>Mauris non nisi volutpat lectus auctor varius. Ut laoreet tristique egestas gravida felis scelerisque egestas. Odio volutpat vitae amet sit molestie at. Magnis velit erat.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>

            {/* Row 2 - Placeholder cards as shown in Figma */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder gray background */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Nisl id ultrices erat ultrices</h3>
                <p>Morbi tincidunt sagittis tristique dignissim nunc gravida lectus sagittis. Vulputate elementum massa dictum eleifend at faucibus tempor. Fermentum nunc id donec.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder gray background */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Nibh elit vestibulum diam</h3>
                <p>Morbi faucibus urna cras arcu id elementum. Libero leo nam sollicitudin dui faucibus vitae. Eu vitae mollis in ut eget lorem vitae phasellus faucibus. In et arcu venenatis.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder gray background */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Et sem quis integer sagittis</h3>
                <p>Mauris non nisi volutpat lectus auctor varius. Ut laoreet tristique egestas gravida felis scelerisque egestas. Odio volutpat vitae amet sit molestie at. Magnis velit erat.</p>
                <div className="meetings-contact-link-wrapper">
                  <span className="meetings-contact-link">CONTACT US</span>
                  <img 
                    src="/assets/images/meetings-events/d9f5953bd987475125b5858f199be376ac773935.svg" 
                    alt="" 
                    className="link-underline-svg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diptych Carousel Section - Matching Figma exactly */}
      <section className="meetings-diptych-carousel horizontal-carousel-section" data-scroll-section>
        <div className="meetings-diptych-carousel__container" id="draggable-carousel">
          {/* First Diptych - Text with Small Image + Large Image */}
          <div className="meetings-diptych">
            <div className="meetings-diptych__text-panel">
              <div className="meetings-diptych__checklist">
                <div className="meetings-checklist-item">
                  <span>Ut id aenean vestibulum purus. Fringilla.</span>
                </div>
                <div className="meetings-checklist-item">
                  <span>Vitae ac orci volutpat senectus</span>
                </div>
                <div className="meetings-checklist-item">
                  <span>Venenatis cras tempor elementum hac.</span>
                </div>
                <div className="meetings-checklist-item">
                  <span>At felis eu facilisis blandit nibh volutpat.</span>
                </div>
              </div>
              <div className="meetings-diptych__small-image">
                <img src="/assets/images/meetings-events/c33b334690d591338bf28c1e9153c04d11f8dba5.png" alt="Small detail" />
              </div>
            </div>
            <div className="meetings-diptych__image-panel">
              <img 
                src="/assets/images/meetings-events/6200c3abfabed50d490407fe1f8df401c703ad98.png" 
                alt="Atacama landscape" 
                className="meetings-diptych__image"
              />
            </div>
          </div>

          {/* Scattered Collage Section */}
          <div className="meetings-scattered-collage">
            <div className="meetings-scattered__image meetings-scattered__image--1">
              <img src="/assets/images/meetings-events/0a1dff6a9e6afa9f3a7b16c78deaffb435413058.png" alt="Interior space" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--2">
              <img src="/assets/images/meetings-events/b4326814b9fb849f82a940e0796e511a7be7446b.png" alt="Meeting room" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--3">
              <img src="/assets/images/meetings-events/4f8b22055f18ee8c9dacf02b5b640a93cfb017f0.png" alt="Lounge area" />
            </div>
          </div>

          {/* Single Full Image */}
          <div className="meetings-single-image">
            <img 
              src="/assets/images/meetings-events/33d5b125b1c7c803c9ceee3a7ef98e88d3a63f38.png" 
              alt="Pool area" 
              className="meetings-single-image__img"
            />
          </div>

          {/* Triptych Section */}
          <div className="meetings-triptych">
            <div className="meetings-triptych__image meetings-triptych__image--1">
              <img src="/assets/images/meetings-events/5226d5c176daa595f4db55d4de23caf126e503b3.png" alt="Terrace view" />
            </div>
            <div className="meetings-triptych__image meetings-triptych__image--2">
              <img src="/assets/images/meetings-events/476a389e59073cbfded36ae33e3246a8855d3f77.png" alt="Interior detail" />
            </div>
            <div className="meetings-triptych__image meetings-triptych__image--3">
              <img src="/assets/images/meetings-events/81debd7e0047e7bf4ae1a23d7db43d63c1b81226.png" alt="Outdoor space" />
            </div>
          </div>

          {/* Final Full Image */}
          <div className="meetings-final-image">
            <img 
              src="/assets/images/meetings-events/d8dbaa08f904fa823929d0f428310920f12625c7.png" 
              alt="Awasi Atacama pool area" 
              className="meetings-final-image__img"
            />
          </div>
        </div>
      </section>

      {/* Contact Us Section with Atacama Background */}
      <section className="meetings-contact-us-section" data-scroll-section>
        <ContactUsAtacama />
      </section>

      {/* Instagram Gallery */}
      <InstagramSection />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default MeetingsEventsAtacama;